// Core mapping for languages / countries we explicitly support
const LANGUAGE_TO_COUNTRY_MAP = {
    ar: 'SA',
    ca: 'ES',
    cn: 'CN',
    cs: 'CZ',
    de: 'DE',
    el: 'GR',
    en: 'US',
    es: 'ES',
    'es-es': 'ES',
    et: 'EE',
    fi: 'FI',
    fr: 'FR',
    he: 'IL',
    hu: 'HU',
    it: 'IT',
    ja: 'JP',
    ko: 'KR',
    lt: 'LT',
    nl: 'NL',
    no: 'NO',
    pl: 'PL',
    'pt-br': 'BR',
    'pt-pt': 'PT',
    ro: 'RO',
    ru: 'RU',
    sk: 'SK',
    sv: 'SE',
    th: 'TH',
    tr: 'TR',
    ua: 'UA',
    vn: 'VN',
    zh: 'CN',
};
// Pre-computed Set of supported language prefixes for performance
const SUPPORTED_LANGUAGE_PREFIXES = new Set(Object.keys(LANGUAGE_TO_COUNTRY_MAP).map((lang) => lang.split('-')[0]));
/**
 * Extracts country code from language-region format (e.g., 'en-US' -> 'US')
 * Only extracts for known language patterns to avoid false positives
 */
const extractCountryFromLanguage = (lang) => {
    const parts = lang.toLowerCase().split('-');
    if (parts.length >= 2) {
        const [language, region] = parts;
        const country = region.toUpperCase();
        // Only extract country codes for languages that are supported in our mapping
        // This prevents extracting from made-up language codes like 'xx-XX'
        const isKnownLanguage = SUPPORTED_LANGUAGE_PREFIXES.has(language);
        if (isKnownLanguage && /^[A-Z]{2,3}$/.test(country)) {
            return country;
        }
    }
    return null;
};
/**
 * Gets the default country code based on browser language
 * @param rawBrowserLang - The raw browser language string (e.g., 'en-US', 'es-MX')
 * @param propertyCountry - The property's country code
 * @returns The default country code to use
 */
export const getDefaultCountryFromRawLanguage = (rawBrowserLang, propertyCountry) => {
    try {
        if (!rawBrowserLang) {
            return propertyCountry || 'US';
        }
        const lang = rawBrowserLang.toLowerCase();
        // Handle special cases that can't be auto-extracted
        if (lang === 'zh-hans')
            return 'CN';
        if (lang === 'zh-hant')
            return 'TW';
        // Try to extract country from language-region format (e.g., "en-US" → "US")
        const extractedCountry = extractCountryFromLanguage(rawBrowserLang);
        if (extractedCountry) {
            return extractedCountry;
        }
        // For non-country-specific languages, prefer the property's country
        if (propertyCountry) {
            return propertyCountry;
        }
        // Fallback to language-to-country mapping (only if no property country)
        if (LANGUAGE_TO_COUNTRY_MAP[lang]) {
            return LANGUAGE_TO_COUNTRY_MAP[lang];
        }
        // Final fallback: US
        return 'US';
    }
    catch (error) {
        console.error('Error in getDefaultCountryFromRawLanguage:', error);
        return propertyCountry || 'US';
    }
};
/**
 * Gets the default country from the browser language
 * @param propertyCountry - The property's country code
 * @returns The default country code
 */
export const getDefaultCountryFromBrowser = (propertyCountry) => {
    try {
        if (typeof navigator === 'undefined' || !navigator.language) {
            return propertyCountry || 'US';
        }
        return getDefaultCountryFromRawLanguage(navigator.language, propertyCountry);
    }
    catch (error) {
        console.error('Error in getDefaultCountryFromBrowser:', error);
        return propertyCountry || 'US';
    }
};
